import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {MainLeadDataPayload} from "../../application/types/lead/MainLeadDataPayload";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {IReservationRequest} from "../../reservation/types/IReservationRequest";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {AuthModalVariant} from "../components/auth_modal/AuthModalHeaderVariants";
import {REGISTRATION_WIZARD_STEPS} from "../constants/registration_wizard_steps";
import {AmplitudeAuthLocation} from "../tracking/track_amplitude_auth_event";
import {AuthActionType} from "../types/AuthActionType";
import {RegistrationFlowControlAction} from "../types/RegistrationFlowControlAction";
import {RegistrationFlowStep} from "../types/RegistrationFlowStep";
import {RegistrationAuthMode, RegistrationWizardEntry} from "../types/RegistrationWizardEntry";
import {UnlockChoiceEntryPoint, UnlockChoiceOrigin} from "../types/UnlockChoiceEntryPoint";
import {IAuthModalVariant} from "./auth_modal_slice";

export {RegistrationFlowStep};

export const isRegistrationWizardStep = (step: RegistrationFlowStep): boolean => REGISTRATION_WIZARD_STEPS.some((wizardStep) => wizardStep.step === step);

export type RegistrationFlowVariant = "legacy" | "wizard";

export type UnlockChoiceDecision = "treatment" | "control";

export type RegistrationMethod = "email" | "social" | "sms";
export type SocialMediaType = "google" | "facebook";

export interface IRegistrationFlowUserState {
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    hasPhone?: boolean;
    hasName?: boolean;
}

export interface IRegistrationWizardDrafts {
    email?: string;
    name?: string;
    phone?: {code: number; number: string};
    smsLoginPhone?: {code: number; number: string};
}

export interface IRegistrationFlowPreferences {
    regions: number[];
    price?: number;
    rooms: {lower: number; upper: number};
}

export interface IRegistrationFlowState {
    isActive: boolean;
    runId: number;
    registrationMethod: RegistrationMethod;
    currentStep: RegistrationFlowStep;
    flowVariant: RegistrationFlowVariant | null;
    controlAction: RegistrationFlowControlAction | null;
    closeAfterWizard: boolean;
    modalZIndex?: number;
    wizardEntry?: RegistrationWizardEntry;
    authMode: RegistrationAuthMode | null;
    visitedSteps: RegistrationFlowStep[];
    wizardDrafts: IRegistrationWizardDrafts;
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    hasPhone?: boolean;
    hasName?: boolean;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    gtmClickText?: string;
    entryCtaLabel?: string;
    preferences: IRegistrationFlowPreferences | null;
    modalVariant: IAuthModalVariant;
    lastAuthAction: AuthActionType | null;
    propertyId?: number;
    offerId?: number;
    vendorId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
    useLeadAfterRegistration?: boolean;
    // Set by the flow host for a compass offer - its vendor takes no leads, so the flow ends on the hotline consultant step
    useConsultantFlow?: boolean;
    leadSource?: ApplicationSource;
    leadSourceSection?: ApplicationSourceSection;
    amplitudePlacement?: AmplitudeAuthPlacement | null;
    amplitudeLocation?: AmplitudeAuthLocation;
    unlockChoiceEntryPoint?: UnlockChoiceEntryPoint;
    unlockChoiceOrigin?: UnlockChoiceOrigin;
    unlockChoiceLeadPayload?: MainLeadDataPayload;
    /** The unlock choice variant decided before the flow reached the unlock choice step, null when the step decides it */
    unlockChoiceDecision: UnlockChoiceDecision | null;
    unlockChoiceContentDelivered: boolean;
    unlockChoiceLeadSent: boolean;
    // Survives closeFlow, so the content gates can tell a dismissed unlock choice from a finished flow
    unlockChoiceDismissed: boolean;
    // The reservation the flow continues with once the user is signed in with a verified phone
    reservationRequest?: IReservationRequest;
}

const initialState: IRegistrationFlowState = {
    isActive: false,
    runId: 0,
    registrationMethod: "email",
    currentStep: RegistrationFlowStep.REGISTRATION,
    flowVariant: null,
    controlAction: null,
    closeAfterWizard: false,
    modalZIndex: undefined,
    wizardEntry: undefined,
    authMode: null,
    visitedSteps: [],
    wizardDrafts: {},
    isUserLoggedIn: false,
    hasPhoneVerified: false,
    hasCompleteProfile: false,
    hasPhone: undefined,
    hasName: undefined,
    socialMediaType: undefined,
    userSource: undefined,
    gtmSourceSection: undefined,
    gtmClickText: undefined,
    entryCtaLabel: undefined,
    preferences: null,
    modalVariant: "hidden_prices_variant_b",
    propertyId: undefined,
    offerId: undefined,
    vendorId: undefined,
    lastAuthAction: null,
    titleVariant: AuthModalVariant.HIDDEN_PRICES_VARIANT_B,
    showLoginForm: undefined,
    registrationSuccessCallback: undefined,
    useLeadAfterRegistration: undefined,
    useConsultantFlow: undefined,
    leadSource: undefined,
    leadSourceSection: undefined,
    amplitudePlacement: undefined,
    amplitudeLocation: undefined,
    unlockChoiceEntryPoint: undefined,
    unlockChoiceOrigin: undefined,
    unlockChoiceLeadPayload: undefined,
    unlockChoiceDecision: null,
    unlockChoiceContentDelivered: false,
    unlockChoiceLeadSent: false,
    unlockChoiceDismissed: false,
    reservationRequest: undefined
};

export interface IStartRegistrationFlowPayload extends IRegistrationFlowUserState {
    registrationMethod?: RegistrationMethod;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    gtmClickText?: string;
    entryCtaLabel?: string;
    wizardEntry?: RegistrationWizardEntry;
    flowVariant?: RegistrationFlowVariant;
    controlAction?: RegistrationFlowControlAction;
    closeAfterWizard?: boolean;
    modalZIndex?: number;
    modalVariant?: IAuthModalVariant;
    propertyId?: number;
    offerId?: number;
    vendorId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
    useLeadAfterRegistration?: boolean;
    // Set by an entry point outside the offer and property pages, where the flow host cannot find the compass offer
    useConsultantFlow?: boolean;
    leadSource?: ApplicationSource;
    leadSourceSection?: ApplicationSourceSection;
    amplitudePlacement?: AmplitudeAuthPlacement | null;
    amplitudeLocation?: AmplitudeAuthLocation;
    unlockChoiceEntryPoint?: UnlockChoiceEntryPoint;
    unlockChoiceOrigin?: UnlockChoiceOrigin;
    unlockChoiceLeadPayload?: MainLeadDataPayload;
    reservationRequest?: IReservationRequest;
}

interface IApplyRegistrationFlowVariantPayload {
    variant: RegistrationFlowVariant;
    user: IRegistrationFlowUserState;
    unlockChoiceDecision?: UnlockChoiceDecision | null;
}

export const getLegacyStepAfterUnlock = ({useLeadAfterRegistration}: {useLeadAfterRegistration?: boolean}): RegistrationFlowStep =>
    useLeadAfterRegistration ? RegistrationFlowStep.LEAD_AFTER_REGISTRATION : RegistrationFlowStep.PREFERENCES;

// A started reservation wins over the unlock choice and the post-registration steps
const getStepAfterUnlock = (state: {
    useLeadAfterRegistration?: boolean;
    useConsultantFlow?: boolean;
    unlockChoiceEntryPoint?: UnlockChoiceEntryPoint;
    reservationRequest?: IReservationRequest;
}): RegistrationFlowStep => {
    if (state.useConsultantFlow) {
        return RegistrationFlowStep.HOTLINE_CONSULTANT;
    }

    if (state.reservationRequest) {
        return RegistrationFlowStep.RESERVATION;
    }

    return state.unlockChoiceEntryPoint ? RegistrationFlowStep.UNLOCK_CHOICE : getLegacyStepAfterUnlock(state);
};

const resolveAuthStep = (state: IRegistrationFlowState, user: IRegistrationFlowUserState, variant: RegistrationFlowVariant): RegistrationFlowStep => {
    const isWizard = variant === "wizard";

    if (!user.isUserLoggedIn) {
        return RegistrationFlowStep.REGISTRATION;
    }

    if (!(isWizard ? (user.hasPhone ?? user.hasCompleteProfile) : user.hasCompleteProfile)) {
        return RegistrationFlowStep.PHONE_COMPLETION;
    }

    if (!user.hasPhoneVerified) {
        return RegistrationFlowStep.PHONE_VERIFICATION;
    }

    if (isWizard && !(user.hasName ?? user.hasCompleteProfile)) {
        return RegistrationFlowStep.NAME_COMPLETION;
    }

    return getStepAfterUnlock(state);
};

const USER_STATE_AFTER_AUTH_STEP: Record<RegistrationFlowVariant, Partial<Record<RegistrationFlowStep, IRegistrationFlowUserState>>> = {
    legacy: {
        [RegistrationFlowStep.REGISTRATION]: {isUserLoggedIn: true},
        [RegistrationFlowStep.PHONE_COMPLETION]: {isUserLoggedIn: true, hasCompleteProfile: true},
        [RegistrationFlowStep.PHONE_VERIFICATION]: {isUserLoggedIn: true, hasCompleteProfile: true, hasPhoneVerified: true}
    },
    wizard: {
        [RegistrationFlowStep.REGISTRATION]: {isUserLoggedIn: true},
        [RegistrationFlowStep.PHONE_COMPLETION]: {isUserLoggedIn: true, hasPhone: true},
        [RegistrationFlowStep.PHONE_VERIFICATION]: {isUserLoggedIn: true, hasPhone: true, hasPhoneVerified: true},
        [RegistrationFlowStep.NAME_COMPLETION]: {isUserLoggedIn: true, hasPhone: true, hasPhoneVerified: true, hasName: true}
    }
};

export const isRegistrationFlowVariantPending = (
    flowState: Pick<IRegistrationFlowState, "isActive" | "flowVariant" | "controlAction" | "wizardEntry" | "currentStep">
): boolean =>
    flowState.isActive &&
    flowState.flowVariant === null &&
    (Boolean(flowState.controlAction) || (Boolean(flowState.wizardEntry) && isRegistrationWizardStep(flowState.currentStep)));

export const isRegistrationWizardRunActive = (flowState: Pick<IRegistrationFlowState, "isActive" | "flowVariant">): boolean =>
    flowState.isActive && flowState.flowVariant === "wizard";

export const isRegistrationWizardStepShown = (flowState: Pick<IRegistrationFlowState, "isActive" | "flowVariant" | "currentStep">): boolean =>
    isRegistrationWizardRunActive(flowState) && isRegistrationWizardStep(flowState.currentStep);

export const isRegistrationWizardFlowActive = (
    flowState: Pick<IRegistrationFlowState, "isActive" | "flowVariant" | "controlAction" | "wizardEntry" | "currentStep">
): boolean => isRegistrationWizardRunActive(flowState) || isRegistrationFlowVariantPending(flowState);

export const getPreviousRegistrationWizardStep = (currentStep: RegistrationFlowStep, visitedSteps: RegistrationFlowStep[]): RegistrationFlowStep | null => {
    if (currentStep === RegistrationFlowStep.PHONE_VERIFICATION) {
        return RegistrationFlowStep.PHONE_COMPLETION;
    }

    if (currentStep === RegistrationFlowStep.NAME_COMPLETION) {
        return null;
    }

    const currentIndex = REGISTRATION_WIZARD_STEPS.findIndex((wizardStep) => wizardStep.step === currentStep);

    for (let index = currentIndex - 1; index >= 0; index--) {
        const {step} = REGISTRATION_WIZARD_STEPS[index];

        if (step !== RegistrationFlowStep.PHONE_VERIFICATION && visitedSteps.includes(step)) {
            return step;
        }
    }

    return null;
};

const moveToStep = (state: IRegistrationFlowState, step: RegistrationFlowStep) => {
    state.currentStep = step;

    if (!state.visitedSteps.includes(step)) {
        state.visitedSteps.push(step);
    }
};

const getClosedState = (state: IRegistrationFlowState): IRegistrationFlowState => ({
    ...initialState,
    runId: state.runId,
    unlockChoiceDismissed: state.unlockChoiceDismissed
});

const closeIfWizardIsOver = (state: IRegistrationFlowState) => {
    if (state.closeAfterWizard && state.flowVariant === "wizard" && !isRegistrationWizardStep(state.currentStep)) {
        Object.assign(state, getClosedState(state));
    }
};

export const registrationFlowSlice = createSlice({
    name: "registrationFlow",
    initialState,
    reducers: {
        startRegistrationFlow: (state, action: PayloadAction<IStartRegistrationFlowPayload>) => {
            state.isActive = true;
            state.runId += 1;
            state.registrationMethod = action.payload.registrationMethod ?? initialState.registrationMethod;
            state.isUserLoggedIn = action.payload.isUserLoggedIn;
            state.hasCompleteProfile = action.payload.hasCompleteProfile;
            state.hasPhoneVerified = action.payload.hasPhoneVerified;
            state.hasPhone = action.payload.hasPhone;
            state.hasName = action.payload.hasName;
            state.socialMediaType = action.payload.socialMediaType;
            state.userSource = action.payload.userSource;
            state.gtmSourceSection = action.payload.gtmSourceSection;
            state.gtmClickText = action.payload.gtmClickText;
            state.entryCtaLabel = action.payload.entryCtaLabel;
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
            state.vendorId = action.payload.vendorId;
            state.titleVariant = action.payload.titleVariant;
            state.showLoginForm = action.payload.showLoginForm;
            state.registrationSuccessCallback = action.payload.registrationSuccessCallback;
            state.useLeadAfterRegistration = action.payload.useLeadAfterRegistration;
            state.useConsultantFlow = action.payload.useConsultantFlow;
            state.leadSource = action.payload.leadSource;
            state.leadSourceSection = action.payload.leadSourceSection;
            state.amplitudePlacement = action.payload.amplitudePlacement;
            state.amplitudeLocation = action.payload.amplitudeLocation;
            state.modalVariant = action.payload.modalVariant ?? initialState.modalVariant;
            state.unlockChoiceEntryPoint = action.payload.unlockChoiceEntryPoint;
            state.unlockChoiceOrigin = action.payload.unlockChoiceEntryPoint ? (action.payload.unlockChoiceOrigin ?? "gate") : undefined;
            state.unlockChoiceLeadPayload = action.payload.unlockChoiceLeadPayload;
            state.unlockChoiceDecision = initialState.unlockChoiceDecision;
            state.unlockChoiceContentDelivered = initialState.unlockChoiceContentDelivered;
            state.unlockChoiceLeadSent = initialState.unlockChoiceLeadSent;
            state.unlockChoiceDismissed = initialState.unlockChoiceDismissed;
            state.wizardEntry = action.payload.wizardEntry ?? action.payload.unlockChoiceEntryPoint;
            state.authMode = null;
            state.controlAction = action.payload.controlAction ?? null;
            state.closeAfterWizard = action.payload.closeAfterWizard ?? false;
            state.modalZIndex = action.payload.modalZIndex;
            state.flowVariant = state.controlAction
                ? null
                : (action.payload.flowVariant ?? (state.unlockChoiceEntryPoint && state.unlockChoiceOrigin === "cta" ? "wizard" : null));
            state.wizardDrafts = {};
            state.visitedSteps = [];
            // Read by getStepAfterUnlock through resolveAuthStep, so it has to be set before the starting step is chosen
            state.reservationRequest = action.payload.reservationRequest;
            moveToStep(state, resolveAuthStep(state, action.payload, state.flowVariant === "wizard" || state.controlAction ? "wizard" : "legacy"));

            closeIfWizardIsOver(state);
        },
        applyRegistrationFlowVariant: (state, action: PayloadAction<IApplyRegistrationFlowVariantPayload>) => {
            const {variant, user, unlockChoiceDecision = null} = action.payload;
            const isOnWizardStartStep = state.controlAction !== null;

            state.flowVariant = variant;
            state.controlAction = null;
            state.unlockChoiceDecision = unlockChoiceDecision;

            if (variant === "wizard" && isRegistrationWizardStep(state.currentStep)) {
                state.visitedSteps = [];
                moveToStep(state, resolveAuthStep(state, user, "wizard"));
            }

            if (variant === "legacy" && isOnWizardStartStep && isRegistrationWizardStep(state.currentStep)) {
                const {isUserLoggedIn, hasCompleteProfile, hasPhoneVerified} = state;

                state.visitedSteps = [];
                moveToStep(state, resolveAuthStep(state, {isUserLoggedIn, hasCompleteProfile, hasPhoneVerified}, "legacy"));
            }

            closeIfWizardIsOver(state);
        },
        nextStep: (state, action: PayloadAction<IRegistrationFlowUserState & {hasCompleteProfile: boolean; hasPhoneVerified: boolean}>) => {
            if (state.flowVariant === "wizard" && isRegistrationWizardStep(state.currentStep)) {
                moveToStep(state, resolveAuthStep(state, {...action.payload, ...USER_STATE_AFTER_AUTH_STEP.wizard[state.currentStep]}, "wizard"));
                closeIfWizardIsOver(state);

                return;
            }

            const legacyUserStateAfterStep = USER_STATE_AFTER_AUTH_STEP.legacy[state.currentStep];

            if (legacyUserStateAfterStep) {
                state.currentStep = resolveAuthStep(state, {...action.payload, ...legacyUserStateAfterStep}, "legacy");

                return;
            }

            switch (state.currentStep) {
                case RegistrationFlowStep.LEAD_AFTER_REGISTRATION:
                case RegistrationFlowStep.UNLOCK_CHOICE:
                case RegistrationFlowStep.HOTLINE_CONSULTANT:
                    state.currentStep = RegistrationFlowStep.PREFERENCES;
                    break;
                case RegistrationFlowStep.MULTILEAD:
                    state.currentStep = RegistrationFlowStep.PREFERENCES;
                    break;
                case RegistrationFlowStep.PREFERENCES:
                    state.currentStep = RegistrationFlowStep.UPDATED_PREFERENCES;
                    break;
                default:
                    state.isActive = false;
            }
        },
        setStep: (state, action: PayloadAction<{step: RegistrationFlowStep}>) => {
            moveToStep(state, action.payload.step);
        },
        goToPreviousStep: (state) => {
            const previousStep = getPreviousRegistrationWizardStep(state.currentStep, state.visitedSteps);

            if (previousStep) {
                moveToStep(state, previousStep);
            }
        },
        saveWizardDraft: (state, action: PayloadAction<IRegistrationWizardDrafts>) => {
            if (!state.isActive) {
                return;
            }

            state.wizardDrafts = {...state.wizardDrafts, ...action.payload};
        },
        // Continues a running flow with the reservation code step, e.g. from the modal shown after the registration
        setReservationStep: (state, action: PayloadAction<{reservationRequest: IReservationRequest}>) => {
            state.reservationRequest = action.payload.reservationRequest;
            moveToStep(state, RegistrationFlowStep.RESERVATION);
        },
        savePreferences: (state, action: PayloadAction<IRegistrationFlowPreferences>) => {
            state.preferences = action.payload;
        },
        closeFlow: (state) => getClosedState(state),
        setRegistrationAuthMode: (state, action: PayloadAction<{authMode: RegistrationAuthMode}>) => {
            state.authMode = action.payload.authMode;
        },
        setLastAuthAction: (state, action: PayloadAction<{authAction: AuthActionType}>) => {
            const {authAction} = action.payload;
            state.lastAuthAction = authAction;
        },
        setRegistrationMethod: (state, action: PayloadAction<{method: RegistrationMethod}>) => {
            state.registrationMethod = action.payload.method;
        },
        setPropertyAndOfferIds: (state, action: PayloadAction<{propertyId?: number; offerId?: number}>) => {
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
        },
        setVendorId: (state, action: PayloadAction<{vendorId: number}>) => {
            state.vendorId = action.payload.vendorId;
        },
        markUnlockChoiceContentDelivered: (state) => {
            state.unlockChoiceContentDelivered = true;
        },
        markUnlockChoiceLeadSent: (state) => {
            state.unlockChoiceLeadSent = true;
        },
        markUnlockChoiceDismissed: (state) => {
            state.unlockChoiceDismissed = true;
        },
        // A flow that is already past the auth steps (e.g. a logged-in user) moves straight to the hotline consultant
        enableConsultantFlow: (state) => {
            state.useConsultantFlow = true;

            if (!isRegistrationWizardStep(state.currentStep)) {
                moveToStep(state, RegistrationFlowStep.HOTLINE_CONSULTANT);
            }
        }
    }
});

export const {
    startRegistrationFlow,
    applyRegistrationFlowVariant,
    nextStep,
    setStep,
    goToPreviousStep,
    saveWizardDraft,
    setReservationStep,
    savePreferences,
    closeFlow,
    setRegistrationAuthMode,
    setLastAuthAction,
    setRegistrationMethod,
    setPropertyAndOfferIds,
    setVendorId,
    markUnlockChoiceContentDelivered,
    markUnlockChoiceLeadSent,
    markUnlockChoiceDismissed,
    enableConsultantFlow
} = registrationFlowSlice.actions;

export const startRegistrationFlowByVariant = (
    flow: Omit<IStartRegistrationFlowPayload, "flowVariant" | "controlAction">,
    controlAction: RegistrationFlowControlAction
) => startRegistrationFlow({...flow, controlAction});
import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {MainLeadDataPayload} from "../../application/types/lead/MainLeadDataPayload";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {IReservationRequest} from "../../reservation/types/IReservationRequest";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {AuthModalVariant} from "../components/auth_modal/AuthModalHeaderVariants";
import {REGISTRATION_WIZARD_STEPS} from "../constants/registration_wizard_steps";
import {AmplitudeAuthLocation} from "../tracking/track_amplitude_auth_event";
import {AuthActionType} from "../types/AuthActionType";
import {RegistrationFlowControlAction} from "../types/RegistrationFlowControlAction";
import {RegistrationFlowStep} from "../types/RegistrationFlowStep";
import {RegistrationAuthMode, RegistrationWizardEntry} from "../types/RegistrationWizardEntry";
import {UnlockChoiceEntryPoint, UnlockChoiceOrigin} from "../types/UnlockChoiceEntryPoint";
import {IAuthModalVariant} from "./auth_modal_slice";

export {RegistrationFlowStep};

export const isRegistrationWizardStep = (step: RegistrationFlowStep): boolean => REGISTRATION_WIZARD_STEPS.some((wizardStep) => wizardStep.step === step);

export type RegistrationFlowVariant = "legacy" | "wizard";

export type UnlockChoiceDecision = "treatment" | "control";

export type RegistrationMethod = "email" | "social" | "sms";
export type SocialMediaType = "google" | "facebook";

export interface IRegistrationFlowUserState {
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    hasPhone?: boolean;
    hasName?: boolean;
}

export interface IRegistrationWizardDrafts {
    email?: string;
    name?: string;
    phone?: {code: number; number: string};
    smsLoginPhone?: {code: number; number: string};
}

export interface IRegistrationFlowPreferences {
    regions: number[];
    price?: number;
    rooms: {lower: number; upper: number};
}

export interface IRegistrationFlowState {
    isActive: boolean;
    runId: number;
    registrationMethod: RegistrationMethod;
    currentStep: RegistrationFlowStep;
    flowVariant: RegistrationFlowVariant | null;
    controlAction: RegistrationFlowControlAction | null;
    closeAfterWizard: boolean;
    modalZIndex?: number;
    wizardEntry?: RegistrationWizardEntry;
    authMode: RegistrationAuthMode | null;
    visitedSteps: RegistrationFlowStep[];
    wizardDrafts: IRegistrationWizardDrafts;
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    hasPhone?: boolean;
    hasName?: boolean;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    gtmClickText?: string;
    entryCtaLabel?: string;
    preferences: IRegistrationFlowPreferences | null;
    modalVariant: IAuthModalVariant;
    lastAuthAction: AuthActionType | null;
    propertyId?: number;
    offerId?: number;
    vendorId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
    useLeadAfterRegistration?: boolean;
    // Set by the flow host for a compass offer - its vendor takes no leads, so the flow ends on the hotline consultant step
    useConsultantFlow?: boolean;
    leadSource?: ApplicationSource;
    leadSourceSection?: ApplicationSourceSection;
    amplitudePlacement?: AmplitudeAuthPlacement | null;
    amplitudeLocation?: AmplitudeAuthLocation;
    unlockChoiceEntryPoint?: UnlockChoiceEntryPoint;
    unlockChoiceOrigin?: UnlockChoiceOrigin;
    unlockChoiceLeadPayload?: MainLeadDataPayload;
    /** The unlock choice variant decided before the flow reached the unlock choice step, null when the step decides it */
    unlockChoiceDecision: UnlockChoiceDecision | null;
    unlockChoiceContentDelivered: boolean;
    unlockChoiceLeadSent: boolean;
    // Survives closeFlow, so the content gates can tell a dismissed unlock choice from a finished flow
    unlockChoiceDismissed: boolean;
    // The reservation the flow continues with once the user is signed in with a verified phone
    reservationRequest?: IReservationRequest;
}

const initialState: IRegistrationFlowState = {
    isActive: false,
    runId: 0,
    registrationMethod: "email",
    currentStep: RegistrationFlowStep.REGISTRATION,
    flowVariant: null,
    controlAction: null,
    closeAfterWizard: false,
    modalZIndex: undefined,
    wizardEntry: undefined,
    authMode: null,
    visitedSteps: [],
    wizardDrafts: {},
    isUserLoggedIn: false,
    hasPhoneVerified: false,
    hasCompleteProfile: false,
    hasPhone: undefined,
    hasName: undefined,
    socialMediaType: undefined,
    userSource: undefined,
    gtmSourceSection: undefined,
    gtmClickText: undefined,
    entryCtaLabel: undefined,
    preferences: null,
    modalVariant: "hidden_prices_variant_b",
    propertyId: undefined,
    offerId: undefined,
    vendorId: undefined,
    lastAuthAction: null,
    titleVariant: AuthModalVariant.HIDDEN_PRICES_VARIANT_B,
    showLoginForm: undefined,
    registrationSuccessCallback: undefined,
    useLeadAfterRegistration: undefined,
    useConsultantFlow: undefined,
    leadSource: undefined,
    leadSourceSection: undefined,
    amplitudePlacement: undefined,
    amplitudeLocation: undefined,
    unlockChoiceEntryPoint: undefined,
    unlockChoiceOrigin: undefined,
    unlockChoiceLeadPayload: undefined,
    unlockChoiceDecision: null,
    unlockChoiceContentDelivered: false,
    unlockChoiceLeadSent: false,
    unlockChoiceDismissed: false,
    reservationRequest: undefined
};

export interface IStartRegistrationFlowPayload extends IRegistrationFlowUserState {
    registrationMethod?: RegistrationMethod;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    gtmClickText?: string;
    entryCtaLabel?: string;
    wizardEntry?: RegistrationWizardEntry;
    flowVariant?: RegistrationFlowVariant;
    controlAction?: RegistrationFlowControlAction;
    closeAfterWizard?: boolean;
    modalZIndex?: number;
    modalVariant?: IAuthModalVariant;
    propertyId?: number;
    offerId?: number;
    vendorId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
    useLeadAfterRegistration?: boolean;
    // Set by an entry point outside the offer and property pages, where the flow host cannot find the compass offer
    useConsultantFlow?: boolean;
    leadSource?: ApplicationSource;
    leadSourceSection?: ApplicationSourceSection;
    amplitudePlacement?: AmplitudeAuthPlacement | null;
    amplitudeLocation?: AmplitudeAuthLocation;
    unlockChoiceEntryPoint?: UnlockChoiceEntryPoint;
    unlockChoiceOrigin?: UnlockChoiceOrigin;
    unlockChoiceLeadPayload?: MainLeadDataPayload;
    reservationRequest?: IReservationRequest;
}

interface IApplyRegistrationFlowVariantPayload {
    variant: RegistrationFlowVariant;
    user: IRegistrationFlowUserState;
    unlockChoiceDecision?: UnlockChoiceDecision | null;
}

export const getLegacyStepAfterUnlock = ({useLeadAfterRegistration}: {useLeadAfterRegistration?: boolean}): RegistrationFlowStep =>
    useLeadAfterRegistration ? RegistrationFlowStep.LEAD_AFTER_REGISTRATION : RegistrationFlowStep.PREFERENCES;

// A started reservation wins over the unlock choice and the post-registration steps
const getStepAfterUnlock = (state: {
    useLeadAfterRegistration?: boolean;
    useConsultantFlow?: boolean;
    unlockChoiceEntryPoint?: UnlockChoiceEntryPoint;
    reservationRequest?: IReservationRequest;
}): RegistrationFlowStep => {
    if (state.useConsultantFlow) {
        return RegistrationFlowStep.HOTLINE_CONSULTANT;
    }

    if (state.reservationRequest) {
        return RegistrationFlowStep.RESERVATION;
    }

    return state.unlockChoiceEntryPoint ? RegistrationFlowStep.UNLOCK_CHOICE : getLegacyStepAfterUnlock(state);
};

const resolveAuthStep = (state: IRegistrationFlowState, user: IRegistrationFlowUserState, variant: RegistrationFlowVariant): RegistrationFlowStep => {
    const isWizard = variant === "wizard";

    if (!user.isUserLoggedIn) {
        return RegistrationFlowStep.REGISTRATION;
    }

    if (!(isWizard ? (user.hasPhone ?? user.hasCompleteProfile) : user.hasCompleteProfile)) {
        return RegistrationFlowStep.PHONE_COMPLETION;
    }

    if (!user.hasPhoneVerified) {
        return RegistrationFlowStep.PHONE_VERIFICATION;
    }

    if (isWizard && !(user.hasName ?? user.hasCompleteProfile)) {
        return RegistrationFlowStep.NAME_COMPLETION;
    }

    return getStepAfterUnlock(state);
};

const USER_STATE_AFTER_AUTH_STEP: Record<RegistrationFlowVariant, Partial<Record<RegistrationFlowStep, IRegistrationFlowUserState>>> = {
    legacy: {
        [RegistrationFlowStep.REGISTRATION]: {isUserLoggedIn: true},
        [RegistrationFlowStep.PHONE_COMPLETION]: {isUserLoggedIn: true, hasCompleteProfile: true},
        [RegistrationFlowStep.PHONE_VERIFICATION]: {isUserLoggedIn: true, hasCompleteProfile: true, hasPhoneVerified: true}
    },
    wizard: {
        [RegistrationFlowStep.REGISTRATION]: {isUserLoggedIn: true},
        [RegistrationFlowStep.PHONE_COMPLETION]: {isUserLoggedIn: true, hasPhone: true},
        [RegistrationFlowStep.PHONE_VERIFICATION]: {isUserLoggedIn: true, hasPhone: true, hasPhoneVerified: true},
        [RegistrationFlowStep.NAME_COMPLETION]: {isUserLoggedIn: true, hasPhone: true, hasPhoneVerified: true, hasName: true}
    }
};

export const isRegistrationFlowVariantPending = (
    flowState: Pick<IRegistrationFlowState, "isActive" | "flowVariant" | "controlAction" | "wizardEntry" | "currentStep">
): boolean =>
    flowState.isActive &&
    flowState.flowVariant === null &&
    (Boolean(flowState.controlAction) || (Boolean(flowState.wizardEntry) && isRegistrationWizardStep(flowState.currentStep)));

export const isRegistrationWizardRunActive = (flowState: Pick<IRegistrationFlowState, "isActive" | "flowVariant">): boolean =>
    flowState.isActive && flowState.flowVariant === "wizard";

export const isRegistrationWizardStepShown = (flowState: Pick<IRegistrationFlowState, "isActive" | "flowVariant" | "currentStep">): boolean =>
    isRegistrationWizardRunActive(flowState) && isRegistrationWizardStep(flowState.currentStep);

export const isRegistrationWizardFlowActive = (
    flowState: Pick<IRegistrationFlowState, "isActive" | "flowVariant" | "controlAction" | "wizardEntry" | "currentStep">
): boolean => isRegistrationWizardRunActive(flowState) || isRegistrationFlowVariantPending(flowState);

export const getPreviousRegistrationWizardStep = (currentStep: RegistrationFlowStep, visitedSteps: RegistrationFlowStep[]): RegistrationFlowStep | null => {
    if (currentStep === RegistrationFlowStep.PHONE_VERIFICATION) {
        return RegistrationFlowStep.PHONE_COMPLETION;
    }

    if (currentStep === RegistrationFlowStep.NAME_COMPLETION) {
        return null;
    }

    const currentIndex = REGISTRATION_WIZARD_STEPS.findIndex((wizardStep) => wizardStep.step === currentStep);

    for (let index = currentIndex - 1; index >= 0; index--) {
        const {step} = REGISTRATION_WIZARD_STEPS[index];

        if (step !== RegistrationFlowStep.PHONE_VERIFICATION && visitedSteps.includes(step)) {
            return step;
        }
    }

    return null;
};

const moveToStep = (state: IRegistrationFlowState, step: RegistrationFlowStep) => {
    state.currentStep = step;

    if (!state.visitedSteps.includes(step)) {
        state.visitedSteps.push(step);
    }
};

const getClosedState = (state: IRegistrationFlowState): IRegistrationFlowState => ({
    ...initialState,
    runId: state.runId,
    unlockChoiceDismissed: state.unlockChoiceDismissed
});

const closeIfWizardIsOver = (state: IRegistrationFlowState) => {
    if (state.closeAfterWizard && state.flowVariant === "wizard" && !isRegistrationWizardStep(state.currentStep)) {
        Object.assign(state, getClosedState(state));
    }
};

export const registrationFlowSlice = createSlice({
    name: "registrationFlow",
    initialState,
    reducers: {
        startRegistrationFlow: (state, action: PayloadAction<IStartRegistrationFlowPayload>) => {
            state.isActive = true;
            state.runId += 1;
            state.registrationMethod = action.payload.registrationMethod ?? initialState.registrationMethod;
            state.isUserLoggedIn = action.payload.isUserLoggedIn;
            state.hasCompleteProfile = action.payload.hasCompleteProfile;
            state.hasPhoneVerified = action.payload.hasPhoneVerified;
            state.hasPhone = action.payload.hasPhone;
            state.hasName = action.payload.hasName;
            state.socialMediaType = action.payload.socialMediaType;
            state.userSource = action.payload.userSource;
            state.gtmSourceSection = action.payload.gtmSourceSection;
            state.gtmClickText = action.payload.gtmClickText;
            state.entryCtaLabel = action.payload.entryCtaLabel;
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
            state.vendorId = action.payload.vendorId;
            state.titleVariant = action.payload.titleVariant;
            state.showLoginForm = action.payload.showLoginForm;
            state.registrationSuccessCallback = action.payload.registrationSuccessCallback;
            state.useLeadAfterRegistration = action.payload.useLeadAfterRegistration;
            state.useConsultantFlow = action.payload.useConsultantFlow;
            state.leadSource = action.payload.leadSource;
            state.leadSourceSection = action.payload.leadSourceSection;
            state.amplitudePlacement = action.payload.amplitudePlacement;
            state.amplitudeLocation = action.payload.amplitudeLocation;
            state.modalVariant = action.payload.modalVariant ?? initialState.modalVariant;
            state.unlockChoiceEntryPoint = action.payload.unlockChoiceEntryPoint;
            state.unlockChoiceOrigin = action.payload.unlockChoiceEntryPoint ? (action.payload.unlockChoiceOrigin ?? "gate") : undefined;
            state.unlockChoiceLeadPayload = action.payload.unlockChoiceLeadPayload;
            state.unlockChoiceDecision = initialState.unlockChoiceDecision;
            state.unlockChoiceContentDelivered = initialState.unlockChoiceContentDelivered;
            state.unlockChoiceLeadSent = initialState.unlockChoiceLeadSent;
            state.unlockChoiceDismissed = initialState.unlockChoiceDismissed;
            state.wizardEntry = action.payload.wizardEntry ?? action.payload.unlockChoiceEntryPoint;
            state.authMode = null;
            state.controlAction = action.payload.controlAction ?? null;
            state.closeAfterWizard = action.payload.closeAfterWizard ?? false;
            state.modalZIndex = action.payload.modalZIndex;
            state.flowVariant = state.controlAction
                ? null
                : (action.payload.flowVariant ?? (state.unlockChoiceEntryPoint && state.unlockChoiceOrigin === "cta" ? "wizard" : null));
            state.wizardDrafts = {};
            state.visitedSteps = [];
            // Read by getStepAfterUnlock through resolveAuthStep, so it has to be set before the starting step is chosen
            state.reservationRequest = action.payload.reservationRequest;
            moveToStep(state, resolveAuthStep(state, action.payload, state.flowVariant === "wizard" || state.controlAction ? "wizard" : "legacy"));

            closeIfWizardIsOver(state);
        },
        applyRegistrationFlowVariant: (state, action: PayloadAction<IApplyRegistrationFlowVariantPayload>) => {
            const {variant, user, unlockChoiceDecision = null} = action.payload;
            const isOnWizardStartStep = state.controlAction !== null;

            state.flowVariant = variant;
            state.controlAction = null;
            state.unlockChoiceDecision = unlockChoiceDecision;

            if (variant === "wizard" && isRegistrationWizardStep(state.currentStep)) {
                state.visitedSteps = [];
                moveToStep(state, resolveAuthStep(state, user, "wizard"));
            }

            if (variant === "legacy" && isOnWizardStartStep && isRegistrationWizardStep(state.currentStep)) {
                const {isUserLoggedIn, hasCompleteProfile, hasPhoneVerified} = state;

                state.visitedSteps = [];
                moveToStep(state, resolveAuthStep(state, {isUserLoggedIn, hasCompleteProfile, hasPhoneVerified}, "legacy"));
            }

            closeIfWizardIsOver(state);
        },
        nextStep: (state, action: PayloadAction<IRegistrationFlowUserState & {hasCompleteProfile: boolean; hasPhoneVerified: boolean}>) => {
            if (state.flowVariant === "wizard" && isRegistrationWizardStep(state.currentStep)) {
                moveToStep(state, resolveAuthStep(state, {...action.payload, ...USER_STATE_AFTER_AUTH_STEP.wizard[state.currentStep]}, "wizard"));
                closeIfWizardIsOver(state);

                return;
            }

            const legacyUserStateAfterStep = USER_STATE_AFTER_AUTH_STEP.legacy[state.currentStep];

            if (legacyUserStateAfterStep) {
                state.currentStep = resolveAuthStep(state, {...action.payload, ...legacyUserStateAfterStep}, "legacy");

                return;
            }

            switch (state.currentStep) {
                case RegistrationFlowStep.LEAD_AFTER_REGISTRATION:
                case RegistrationFlowStep.UNLOCK_CHOICE:
                case RegistrationFlowStep.HOTLINE_CONSULTANT:
                    state.currentStep = RegistrationFlowStep.PREFERENCES;
                    break;
                case RegistrationFlowStep.MULTILEAD:
                    state.currentStep = RegistrationFlowStep.PREFERENCES;
                    break;
                case RegistrationFlowStep.PREFERENCES:
                    state.currentStep = RegistrationFlowStep.UPDATED_PREFERENCES;
                    break;
                default:
                    state.isActive = false;
            }
        },
        setStep: (state, action: PayloadAction<{step: RegistrationFlowStep}>) => {
            moveToStep(state, action.payload.step);
        },
        goToPreviousStep: (state) => {
            const previousStep = getPreviousRegistrationWizardStep(state.currentStep, state.visitedSteps);

            if (previousStep) {
                moveToStep(state, previousStep);
            }
        },
        saveWizardDraft: (state, action: PayloadAction<IRegistrationWizardDrafts>) => {
            if (!state.isActive) {
                return;
            }

            state.wizardDrafts = {...state.wizardDrafts, ...action.payload};
        },
        // Continues a running flow with the reservation code step, e.g. from the modal shown after the registration
        setReservationStep: (state, action: PayloadAction<{reservationRequest: IReservationRequest}>) => {
            state.reservationRequest = action.payload.reservationRequest;
            moveToStep(state, RegistrationFlowStep.RESERVATION);
        },
        savePreferences: (state, action: PayloadAction<IRegistrationFlowPreferences>) => {
            state.preferences = action.payload;
        },
        closeFlow: (state) => getClosedState(state),
        setRegistrationAuthMode: (state, action: PayloadAction<{authMode: RegistrationAuthMode}>) => {
            state.authMode = action.payload.authMode;
        },
        setLastAuthAction: (state, action: PayloadAction<{authAction: AuthActionType}>) => {
            const {authAction} = action.payload;
            state.lastAuthAction = authAction;
        },
        setRegistrationMethod: (state, action: PayloadAction<{method: RegistrationMethod}>) => {
            state.registrationMethod = action.payload.method;
        },
        setPropertyAndOfferIds: (state, action: PayloadAction<{propertyId?: number; offerId?: number}>) => {
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
        },
        setVendorId: (state, action: PayloadAction<{vendorId: number}>) => {
            state.vendorId = action.payload.vendorId;
        },
        markUnlockChoiceContentDelivered: (state) => {
            state.unlockChoiceContentDelivered = true;
        },
        markUnlockChoiceLeadSent: (state) => {
            state.unlockChoiceLeadSent = true;
        },
        markUnlockChoiceDismissed: (state) => {
            state.unlockChoiceDismissed = true;
        },
        // A flow that is already past the auth steps (e.g. a logged-in user) moves straight to the hotline consultant
        enableConsultantFlow: (state) => {
            state.useConsultantFlow = true;

            if (!isRegistrationWizardStep(state.currentStep)) {
                moveToStep(state, RegistrationFlowStep.HOTLINE_CONSULTANT);
            }
        }
    }
});

export const {
    startRegistrationFlow,
    applyRegistrationFlowVariant,
    nextStep,
    setStep,
    goToPreviousStep,
    saveWizardDraft,
    setReservationStep,
    savePreferences,
    closeFlow,
    setRegistrationAuthMode,
    setLastAuthAction,
    setRegistrationMethod,
    setPropertyAndOfferIds,
    setVendorId,
    markUnlockChoiceContentDelivered,
    markUnlockChoiceLeadSent,
    markUnlockChoiceDismissed,
    enableConsultantFlow
} = registrationFlowSlice.actions;

export const startRegistrationFlowByVariant = (
    flow: Omit<IStartRegistrationFlowPayload, "flowVariant" | "controlAction">,
    controlAction: RegistrationFlowControlAction
) => startRegistrationFlow({...flow, controlAction});
